import React, { useState, useCallback, useEffect, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import FormCard from "../FormComponents/FormCard";
import FormGroup from "../FormComponents/FormGroup";
import FormGrid from "../FormComponents/FormGrid";
import AlertBanner from "../FormComponents/AlertBanner";
import SignatureSection from "../FormComponents/SignatureSection";
import Icon from "../Icons/Icon";

/**
 * Step 10: Exhibitor Contact Update
 *
 * This step allows exhibitors to update their contact information including:
 * - Corporate information (address, phone, fax)
 * - Key contacts for various departments (Sales, Marketing, Accounting, etc.)
 *
 * NOTE: This step uses global contact info (not per-contract).
 * It uses getContactInfo() / saveContactInfo() instead of per-contract endpoints.
 */
const Step10 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
}) => {
  const contacts = [
    { role: "Sales Rep", prefix: "salesRep" },
    { role: "Company President", prefix: "president" },
    { role: "Advertising Contact", prefix: "advertising" },
    { role: "Sales Management", prefix: "salesMgmt" },
    { role: "MSDS Contact", prefix: "msds" },
    { role: "Accounting", prefix: "accounting" },
    { role: "Customer Service", prefix: "customerService" },
    { role: "Catalog Billing", prefix: "catalogBilling" },
    { role: "Shipping Manager", prefix: "shippingMgr" },
  ];

  const buildContactDefaults = () => {
    const defaults = {};
    contacts.forEach(({ prefix }) => {
      ["Name", "Email", "Phone", "Mobile"].forEach((field) => {
        defaults[`${prefix}${field}`] = initialContract?.[`${prefix}${field}`] || "";
      });
    });
    return defaults;
  };

  const [formData, setFormData] = useState({
    noChangesNeeded: initialContract?.noChangesNeeded ?? false,
    corporatePhone: initialContract?.corporatePhone || "",
    ...buildContactDefaults(),
    signatureName: initialContract?.signatureName || "",
    signatureDate: initialContract?.signatureDate || "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
  });

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Exhibitor Contact Update</h2>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <AlertBanner type="info">
          INSTRUCTIONS: PLEASE FILL OUT ALL FIELDS
        </AlertBanner>

        <FormCard>
          <AlertBanner type="info">
            PLEASE NOTE THAT ONCE CONTRACT IS SUBMITTED TO PRODUCT MERCHANT YOU
            WILL BE UNABLE TO MAKE ANY FURTHER EDITS TO CONTRACT. IF ANY CHANGES
            ARE NEEDED PLEASE ALERT YOUR PRODUCT MERCHANT & THEY WILL MAKE THE
            NECESSARY UPDATES.
          </AlertBanner>
        </FormCard>

        <FormCard
          title="Corporate Information"
          icon={<Icon name="building" size={14} />}
        >
          <FormGrid cols={1}>
            <FormGroup label="Corporate Phone No." error={errors.corporatePhone}>
              <div className={`form-phone-input ${errors.corporatePhone ? "input-error" : ""}`}>
                <PhoneInput
                  country="us"
                  value={formData.corporatePhone}
                  onChange={(val) => updateField("corporatePhone", val)}
                  enableSearch
                  disableSearchIcon
                  inputProps={{ name: "corporatePhone" }}
                />
              </div>
            </FormGroup>
          </FormGrid>
        </FormCard>

        {contacts.map((contact, i) => {
          const nameErr = errors[`contacts[${i}].name`];
          const emailErr = errors[`contacts[${i}].email`];
          const phoneErr = errors[`contacts[${i}].phone`];
          return (
            <FormCard key={i}>
              <div className="contact-block-title" style={{ marginBottom: 14 }}>
                {contact.role}
              </div>
              <FormGrid cols={2}>
                <FormGroup label="Name" error={nameErr}>
                  <input
                    className={`form-input ${nameErr ? "input-error" : ""}`}
                    value={formData[`${contact.prefix}Name`]}
                    onChange={(e) => updateField(`${contact.prefix}Name`, e.target.value)}
                  />
                </FormGroup>
                <FormGroup label="Email" error={emailErr}>
                  <input
                    type="email"
                    className={`form-input ${emailErr ? "input-error" : ""}`}
                    value={formData[`${contact.prefix}Email`]}
                    onChange={(e) => updateField(`${contact.prefix}Email`, e.target.value)}
                  />
                </FormGroup>
              </FormGrid>
              <FormGrid cols={2} style={{ marginTop: 10 }}>
                <FormGroup label="Phone" error={phoneErr}>
                  <div className={`form-phone-input ${phoneErr ? "input-error" : ""}`}>
                    <PhoneInput
                      country="us"
                      value={formData[`${contact.prefix}Phone`]}
                      onChange={(val) => updateField(`${contact.prefix}Phone`, val)}
                      enableSearch
                      disableSearchIcon
                      inputProps={{ name: `${contact.prefix}Phone` }}
                    />
                  </div>
                </FormGroup>
                <FormGroup label="Mobile">
                  <div className="form-phone-input">
                    <PhoneInput
                      country="us"
                      value={formData[`${contact.prefix}Mobile`]}
                      onChange={(val) => updateField(`${contact.prefix}Mobile`, val)}
                      enableSearch
                      disableSearchIcon
                      inputProps={{ name: `${contact.prefix}Mobile` }}
                    />
                  </div>
                </FormGroup>
              </FormGrid>
            </FormCard>
          );
        })}

        <SignatureSection
          legal={false}
          name={formData.signatureName}
          date={formData.signatureDate}
          acknowledge={formData.signatureAcknowledge}
          onNameChange={(name) => updateField("signatureName", name)}
          onDateChange={(date) => updateField("signatureDate", date)}
          onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
        />
        {(errors.signatureName || errors.signatureDate) && (
          <div className="step-error-summary" style={{ marginTop: 8 }}>
            {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
            {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default Step10;
